import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { type Membership, meQueryOptions, useLogin, useSwitchOrg } from '~/api/auth';
import { isApiError } from '~/api/client';
import { AuthCard } from '~/app/AuthCard';
import { Button } from '~/ui/Button';
import { Input } from '~/ui/Input';
import { PasswordInput } from '~/ui/PasswordInput';
import { Notice } from '~/ui/Notice';

interface LoginSearch {
  next?: string;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    next:
      typeof search['next'] === 'string' && search['next'].startsWith('/')
        ? search['next']
        : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    // Already signed in: go straight through.
    const me = await context.queryClient.fetchQuery(meQueryOptions).catch(() => null);
    if (me) throw redirect({ to: search.next ?? '/dashboard' });
  },
  component: LoginPage,
});

function LoginPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const login = useLogin();
  const switchOrg = useSwitchOrg();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // When an account has more than one organization, we ask which to open.
  const [choices, setChoices] = useState<Membership[] | null>(null);

  const enter = async () => {
    await qc.fetchQuery(meQueryOptions);
    await navigate({ to: next ?? '/dashboard' });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await login.mutateAsync({ email, password }).catch(() => null);
    if (!res) return;
    if (res.memberships.length > 1) setChoices(res.memberships);
    else await enter();
  };

  const pick = async (workspaceId: string) => {
    await switchOrg.mutateAsync(workspaceId);
    // Reload into the chosen company so every screen fetches its data fresh.
    window.location.assign(next ?? '/dashboard');
  };

  const error = login.error;
  // Offline, rate limits and server trouble are toasted by the global handler (marked
  // `handled`); a wrong password is not, since nobody is signed in yet, so it stays here.
  const message =
    !error || choices || (isApiError(error) && error.handled)
      ? null
      : isApiError(error)
        ? error.status === 401 || error.status === 400
          ? 'That email and password do not match.'
          : error.message
        : 'Something went wrong. Try again.';

  if (choices) {
    const busy = switchOrg.isPending;
    return (
      <AuthCard title="Which organization?" subtitle="Your account is in more than one.">
        <ul className="flex flex-col gap-2">
          {choices.map((c) => (
            <li key={c.workspaceId}>
              <button
                type="button"
                disabled={busy}
                onClick={() => void pick(c.workspaceId)}
                className="flex w-full items-center gap-3 rounded-panel border border-line bg-paper px-4 py-3 text-left hover:border-accent hover:bg-accent-soft/40 disabled:opacity-60"
              >
                <span
                  aria-hidden
                  className="grid size-9 place-items-center rounded-control bg-accent-soft text-accent-ink"
                >
                  <Building2 size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{c.name}</span>
                  <span className="block truncate text-xs text-ink-3 capitalize">{c.role}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-4">
          <Button variant="quiet" onClick={() => setChoices(null)} disabled={busy}>
            Use a different email
          </Button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Sign in to see your day.">
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordInput
          label="Password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {message && <Notice tone="late">{message}</Notice>}
        <Button type="submit" block loading={login.isPending}>
          Sign in
        </Button>
      </form>
      <p className="mt-6 text-sm text-ink-3">
        No password yet? Open the invite link the company sent you. Forgotten it? Ask your manager
        for a new invite link.
      </p>
      <p className="mt-3 text-sm text-ink-3">
        New here?{' '}
        <Link to="/signup" className="text-accent hover:underline">
          Set up your company
        </Link>
        .
      </p>
    </AuthCard>
  );
}
